/**
 * A máquina de arcade da Sala de Descanso — o desenho.
 *
 * Vive fora do `officeArt.ts` pela mesma razão da mesa de jogos: a arte deste
 * escritório é um ficheiro de milhares de linhas, e uma peça nova que se explique
 * a si própria não tem de lá entrar para ser desenhada. Aqui só se importa um
 * `Brush` — redeclarado com a forma exacta do original — o que evita um ciclo de
 * importação e mantém a peça auto-contida.
 *
 * O que uma arcade tem de comunicar em 32×70 pixels é uma coisa só: *aquilo
 * joga-se*. Um armário liso dizia "frigorífico". O que diz arcade são três sinais
 * ao mesmo tempo — a MARQUISE acesa no topo, o ECRÃ com jogo lá dentro, e a
 * CONSOLA a sair para a frente com manípulo e botões. Falte um e a silhueta cai
 * logo para outra coisa qualquer.
 *
 * E o ecrã não mostra um padrão decorativo: mostra o Green Rush — estrada,
 * tracejado, o atrelado da Green Village a ser rebocado e um cone à frente. É a
 * mesma imagem que se vê ao carregar no E, o que faz o móvel ser um convite em
 * vez de um enfeite.
 */

/** O pincel do `officeArt` — redeclarado com a MESMA forma para não importar
 *  o módulo grande só por um tipo (e não criar um ciclo entre os dois). */
export interface Brush {
  r(x: number, y: number, w: number, h: number, color: string): void;
  dither(x: number, y: number, w: number, h: number, a: string, b: string): void;
}

/**
 * A grelha de arte, e a fatia dela que é mesmo pegada.
 *
 * Registada como `{ w: 16, h: 35, detail: 2 }` → 32×70 células de 1 px, e um
 * tile de chão. Os 38 px de cima são o armário a subir por cima do tile que
 * ocupa: é isso que faz uma arcade parecer uma arcade e não um caixote.
 */
export const ARCADE_ART = { w: 32, h: 70, pegada: 32 } as const;

// ── Paleta ──────────────────────────────────────────────────
//
// O armário é escuro de propósito. A sala de descanso já tem madeira, tapete
// creme e sofá; uma máquina bege desaparecia no meio disso. Escuro faz de fundo
// para a única coisa que interessa ser vista à distância — a luz do ecrã e da
// marquise. Os verdes são os da marca (os mesmos do tapete: `#348434` e
// `#79a240`), para a máquina pertencer à casa.

const CORPO = "#232a33";
const CORPO_HI = "#39434f";
const CORPO_SH = "#141922";
const CORPO_DK = "#0a0e14";
const METAL = "#5a6270";
const METAL_HI = "#7d8798";

const VERDE = "#348434";
const VERDE_FILETE = "#79a240";
const VERDE_LUZ = "#5ed17a";
const VERDE_LUZ_HI = "#9df0ae";

const CREME = "#dfcdb0";
const CREME_HI = "#f2e6d2";

const ASFALTO = "#232830";
const ASFALTO_HI = "#2e3540";
const RISCA = "#c9d2dc";
const LARANJA = "#e07a24";
const LARANJA_HI = "#ffb45e";

const VERMELHO = "#d0392f";
const VERMELHO_HI = "#f06a5c";
const AMARELO = "#e8b423";
const AMARELO_HI = "#ffd863";

/**
 * A marquise acesa: um atrelado a fugir para a direita com riscas de velocidade
 * atrás. Vinte e dois pixels de largura não dão para escrever "GREEN RUSH" —
 * dão para DESENHAR o que a palavra quer dizer, que se lê à mesma distância e
 * não fica ilegível quando o mapa está afastado.
 */
function marquise(b: Brush): void {
  // Caixa da marquise: moldura escura, painel aceso lá dentro.
  b.r(4, 2, 24, 11, CORPO_SH);
  b.r(5, 3, 22, 9, VERDE_LUZ);
  // A luz não é chapada: entra por cima e desmaia até baixo.
  b.dither(5, 3, 22, 3, VERDE_LUZ_HI, VERDE_LUZ);
  b.dither(5, 9, 22, 3, VERDE, VERDE_LUZ);
  b.r(5, 3, 22, 1, VERDE_LUZ_HI);

  // Riscas de velocidade — três, de comprimentos diferentes, senão parecem uma
  // grelha em vez de vento.
  b.r(6, 5, 5, 1, CREME_HI);
  b.r(7, 7, 6, 1, CREME_HI);
  b.r(6, 9, 4, 1, CREME_HI);

  // O atrelado: corpo creme, tejadilho verde, uma janela e duas rodas.
  b.r(14, 5, 12, 5, CREME);
  b.r(14, 5, 12, 1, CREME_HI);
  b.r(14, 4, 12, 1, VERDE);
  b.r(16, 7, 3, 2, "#2b3540"); // janela
  b.r(15, 10, 2, 1, CORPO_DK);
  b.r(23, 10, 2, 1, CORPO_DK);
}

/**
 * O ecrã a jogar. Vinte por dezanove pixels, e mesmo assim tem de se ver a
 * ESTRADA (dois limites + tracejado ao meio), o ATRELADO em baixo e um OBSTÁCULO
 * à frente. Sem o obstáculo é uma paisagem; com ele é uma corrida.
 */
function ecra(b: Brush): void {
  const X = 6;
  const Y = 17;
  const W = 20;
  const H = 19;

  b.r(X, Y, W, H, ASFALTO);
  // O asfalto foge para longe: mais claro ao fundo, como se a luz do horizonte
  // batesse lá. É o truque que dá profundidade sem uma única linha de fuga.
  b.dither(X, Y, W, 5, ASFALTO_HI, ASFALTO);

  // Limites da estrada.
  b.r(X, Y, 1, H, RISCA);
  b.r(X + W - 1, Y, 1, H, RISCA);

  // Tracejado central, com o espaçamento a abrir à medida que se aproxima —
  // é assim que se lê "isto está a andar" numa imagem parada.
  b.r(X + 9, Y + 1, 2, 2, RISCA);
  b.r(X + 9, Y + 5, 2, 3, RISCA);
  b.r(X + 9, Y + 11, 2, 3, RISCA);

  // Um cone na faixa da esquerda, à frente de quem joga: bico, faixa branca e
  // base. Sem a faixa branca cinco pixels laranja não se leem como cone.
  b.r(X + 4, Y + 3, 1, 2, LARANJA_HI);
  b.r(X + 3, Y + 5, 3, 1, LARANJA);
  b.r(X + 3, Y + 6, 3, 1, CREME_HI);
  b.r(X + 2, Y + 7, 5, 1, LARANJA);
  b.r(X + 2, Y + 8, 5, 1, CORPO_DK);

  // Um bidão de combustível verde na faixa da direita — o bónus.
  b.r(X + 14, Y + 2, 3, 4, VERDE_FILETE);
  b.r(X + 14, Y + 2, 3, 1, VERDE_LUZ_HI);

  // O reboque do jogador, em baixo ao centro: atrelado creme + cabeça verde.
  b.r(X + 7, Y + 9, 7, 6, CREME);
  b.r(X + 7, Y + 9, 7, 1, CREME_HI);
  b.r(X + 8, Y + 11, 5, 2, "#2b3540");
  b.r(X + 7, Y + 15, 7, 4, VERDE);
  b.r(X + 8, Y + 16, 5, 1, VERDE_FILETE);
  b.r(X + 6, Y + 12, 1, 3, CORPO_DK);
  b.r(X + 14, Y + 12, 1, 3, CORPO_DK);

  // Vidro: um brilho diagonal em cima à esquerda. Sem isto o ecrã parece um
  // buraco pintado; com isto parece um tubo aceso atrás de vidro.
  b.dither(X + 1, Y + 1, 6, 2, RISCA, ASFALTO_HI);
}

/**
 * A consola: sai para a frente do armário (mais larga que ele), com o manípulo
 * à esquerda e dois botões à direita. É a saliência que trava a silhueta e a
 * impede de ser lida como um armário liso.
 */
function consola(b: Brush): void {
  // Tampo e frente, com a aresta de luz entre os dois.
  b.r(1, 39, 30, 5, CORPO_HI);
  b.r(1, 39, 30, 1, METAL_HI);
  b.r(1, 44, 30, 6, CORPO);
  b.r(1, 44, 30, 1, CORPO_SH);
  b.r(1, 49, 30, 1, CORPO_DK);

  // Manípulo: base preta, haste de metal, bola vermelha por cima. A bola sobe
  // até à barra do ecrã de propósito — um manípulo que não sai do tampo não se
  // vê a esta escala.
  b.r(7, 40, 6, 3, CORPO_DK);
  b.r(7, 40, 6, 1, METAL);
  b.r(9, 37, 2, 3, METAL);
  b.r(9, 37, 1, 3, METAL_HI);
  b.r(8, 34, 4, 3, VERMELHO);
  b.r(8, 34, 3, 1, VERMELHO_HI);
  b.r(8, 35, 1, 1, VERMELHO_HI);

  // Dois botões, cada um com a sua argola de metal.
  for (const [bx, cor, hi] of [
    [18, VERMELHO, VERMELHO_HI],
    [23, AMARELO, AMARELO_HI],
  ] as const) {
    b.r(bx, 39, 5, 4, METAL);
    b.r(bx + 1, 40, 3, 2, cor);
    b.r(bx + 1, 40, 2, 1, hi);
  }
}

/**
 * O corpo, dos comandos ao chão: painel lateral com a faixa da Green Village,
 * porta de moedas e o plinto que assenta a máquina no tile.
 */
function corpo(b: Brush): void {
  b.r(4, 50, 24, 17, CORPO);
  b.r(4, 50, 1, 17, CORPO_HI);
  b.r(26, 50, 2, 17, CORPO_SH);
  b.dither(6, 50, 20, 17, CORPO_SH, CORPO);

  // A faixa da marca. Duas linhas, verde forte e filete claro — a mesma leitura
  // do tapete, que é o que amarra a máquina ao resto da casa.
  b.r(5, 52, 21, 3, VERDE);
  b.r(5, 52, 21, 1, VERDE_FILETE);
  b.r(5, 56, 21, 1, VERDE_FILETE);

  // Porta de moedas: chapa, ranhura e a caleira de devolução por baixo.
  b.r(10, 58, 12, 6, METAL);
  b.r(10, 58, 12, 1, METAL_HI);
  b.r(14, 60, 4, 1, CORPO_DK);
  b.r(12, 62, 8, 2, CORPO_DK);
  b.r(12, 62, 8, 1, CORPO_SH);

  // Plinto: mais largo que o corpo, para a máquina não parecer pousada em bico.
  b.r(3, 67, 26, 3, CORPO_DK);
  b.r(3, 67, 26, 1, CORPO_SH);
}

/**
 * A máquina completa, de baixo para cima na ordem em que as peças se tapam:
 * armário → ecrã → consola (que passa por cima da barra do ecrã) → marquise.
 */
export function drawArcade(b: Brush): void {
  // Topo do armário e a coluna que segura a marquise.
  b.r(5, 0, 22, 2, CORPO_SH);
  b.r(5, 0, 22, 1, CORPO_HI);
  b.r(3, 13, 26, 27, CORPO_DK);
  b.r(3, 13, 1, 27, CORPO);
  b.r(27, 13, 2, 27, CORPO_SH);

  marquise(b);
  ecra(b);
  corpo(b);
  consola(b);
}
